import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Row, Col, Button } from "reactstrap";
import FormInput from "../form-components/InputComponent";
import FormSelect from "../form-components/SelectComponent";
import usersApis from "../../api/master/users";
import GstinVerificationModal from "../../components/GstinVerificationModal";

interface SelectOptions {
  label: string;
  value: string;
}

interface RegistrationFormProps {
  onSubmit: (data: any) => void;
  onBack?: () => void;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSubmit,
  onBack,
}) => {
  const { handleSubmit, watch, setValue, trigger, clearErrors, getValues } =
    useFormContext();

  const submitForm = (data: any) => {
    onSubmit(data);
  };

  const businessType = watch("bussinessType");
  const country = watch("country");
  const state = watch("state");

  // Clear fields and validation when business type changes
  useEffect(() => {
    if (businessType === "unregistered") {
      setValue("gstin", "");
      clearErrors("gstin");
    } else if (businessType === "registered") {
      clearErrors("gstin");
    }
  }, [businessType, setValue, clearErrors]);

  const [countries, setCountries] = useState<SelectOptions[]>([]);
  const [states, setStates] = useState<SelectOptions[]>([]);
  const [districtOptions, setDistrictOptions] = useState<SelectOptions[]>([]);
  const [gstinModal, setGstinModal] = useState(false);

  const getCountries = async () => {
    try {
      const _countries = await usersApis.getCountrys();
      if (_countries.status) {
        if (_countries.data.length > 0) {
          const transformedData = _countries.data.map((item: any) => ({
            value: item.countryId.toString(),
            label: item.countryName,
          }));
          setCountries(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const fetchStatesByCountry = async (countryId: string) => {
    try {
      const data = await usersApis.getStatesByCountry(countryId);
      const stateOptions = data?.data?.map((state: any) => ({
        value: state.stateId.toString(),
        label: state.stateName,
      }));
      if (stateOptions.length > 0) setStates(stateOptions);
    } catch (error) {}
  };

  const getDistricts = async (stateID: any) => {
    try {
      const districts = await usersApis.getDistrictsByState(stateID);
      if (districts.status) {
        if (districts.data.length > 0) {
          const transformedData = districts.data.map((item: any) => ({
            value: item.districtId.toString(),
            label: item.districtName,
          }));
          setDistrictOptions(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    if (country) {
      fetchStatesByCountry(country);
    }
  }, [country]);

  useEffect(() => {
    if (state) {
      getDistricts(state);
    }
  }, [state]);

  const isGstVerified = () => {
    const values = getValues();
    return !!values.gstin && !!values.pan;
  };

  useEffect(() => {
    if (businessType === "registered" && !isGstVerified()) {
      setGstinModal(true);
    } else {
      setGstinModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType]);

  const findOptionValue = (label: string, options: SelectOptions[]) => {
    if (!label || !options || options.length === 0) return "";

    const exactMatch = options.find(
      (option) =>
        option.label.toLowerCase().trim() === label.toLowerCase().trim()
    );
    if (exactMatch) return exactMatch.value;

    const partialMatch = options.find(
      (option) =>
        option.label.toLowerCase().includes(label.toLowerCase()) ||
        label.toLowerCase().includes(option.label.toLowerCase())
    );
    return partialMatch ? partialMatch.value : "";
  };

  const handleGSTVerification = async (gstData: any) => {
    clearErrors();

    const countryId = findOptionValue(gstData.country, countries);
    const stateId = findOptionValue(gstData.state, states);

    setValue("gstin", gstData.gstin || "", { shouldValidate: false });
    setValue("pan", gstData.pan || "", { shouldValidate: false });
    setValue("supplierName", gstData.LegalName || "", {
      shouldValidate: false,
    });
    setValue("tradeName", gstData.TradeName || gstData.LegalName || "", {
      shouldValidate: false,
    });
    setValue("city", gstData.city || "", { shouldValidate: false });
    setValue("area", gstData.area || "", { shouldValidate: false });
    setValue("addressLine1", gstData.addressLine1 || "", {
      shouldValidate: false,
    });
    setValue("pincode", gstData.zipPincode || "", { shouldValidate: false });

    if (countryId) {
      setValue("country", countryId, { shouldValidate: false });
    }

    if (stateId) {
      setValue("state", stateId, { shouldValidate: false });

      try {
        const stateIdNum = stateId ? Number(stateId) : 0;
        if (stateIdNum) {
          const districts = await usersApis.getDistrictsByState(stateIdNum);
          if (districts.status && districts.data.length > 0) {
            const transformedDistricts = districts.data.map((item: any) => ({
              value: item.districtId.toString(),
              label: item.districtName,
            }));

            setDistrictOptions(transformedDistricts);

            let districtValue = "";
            if (gstData.district) {
              const districtByName = transformedDistricts.find(
                (option: SelectOptions) =>
                  option.label.toLowerCase().trim() ===
                  gstData.district.toLowerCase().trim()
              );
              const districtByValue = transformedDistricts.find(
                (option: SelectOptions) =>
                  option.value.toLowerCase() === gstData.district.toLowerCase()
              );
              const districtPartial = transformedDistricts.find(
                (option: SelectOptions) =>
                  option.label
                    .toLowerCase()
                    .includes(gstData.district.toLowerCase()) ||
                  gstData.district
                    .toLowerCase()
                    .includes(option.label.toLowerCase())
              );
              districtValue =
                districtByName?.value ||
                districtByValue?.value ||
                districtPartial?.value ||
                "";
            }

            if (districtValue) {
              setValue("district", districtValue, { shouldValidate: false });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching districts:", error);
      }

      setTimeout(() => {
        const fieldsToValidate = [
          "gstin",
          "pan",
          "supplierName",
          "tradeName",
          "country",
          "state",
          "district",
          "city",
          "area",
          "addressLine1",
          "pincode",
        ];

        trigger(fieldsToValidate).then((isValid) => {
          console.log("Validation result after GST data:", isValid);
        });
      }, 300);
    } else {
      setTimeout(() => {
        trigger([
          "gstin",
          "pan",
          "supplierName",
          "tradeName",
          "country",
          "city",
          "area",
          "addressLine1",
          "pincode",
        ]);
      }, 300);
    }
  };

  return (
    <>
      <form
        className="content clearfix"
        onSubmit={handleSubmit(submitForm)}
        style={{
          border: "1px solid #e5e9f2",
          borderRadius: "8px",
          padding: "24px",
          backgroundColor: "#fff",
        }}
      >
        <div className="form-section mb-4">
          <h5 className="mb-3 fw-bold">Legal Information</h5>
          <Row className="gy-4">
            <Col lg="4">
              <FormSelect
                name="bussinessType"
                label="Business Type"
                required={true}
                options={[
                  { label: "Registered", value: "registered" },
                  { label: "Unregistered", value: "unregistered" },
                ]}
                placeholder="Select Business Type"
              />
            </Col>
            {businessType === "registered" && (
              <Col lg="4">
                <FormInput
                  name="gstin"
                  label="GSTIN"
                  required={true}
                  type="text"
                  placeholder="Enter GSTIN"
                  pattern={
                    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/
                  }
                />
              </Col>
            )}
            {(businessType === "registered" ||
              businessType === "unregistered") && (
              <Col lg="4">
                <FormInput
                  name="pan"
                  label="PAN"
                  required
                  type="text"
                  pattern={/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/}
                  placeholder="Enter PAN"
                />
              </Col>
            )}
          </Row>
        </div>

        <div className="form-section mb-4">
          <h5 className="mb-3 fw-bold">General Information</h5>
          <Row className="gy-4">
            <Col lg="4">
              <FormInput
                name="supplierName"
                label="Supplier Name(Legal Name)"
                required={true}
                type="text"
                placeholder="Enter Supplier Name"
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(
                    /[^A-Za-z\s.&/-]/g,
                    ""
                  );
                }}
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="tradeName"
                label="Trade Name"
                required={true}
                type="text"
                placeholder="Enter Trade Name"
                pattern={/^[A-Za-z\s]+$/}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                }}
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="brandName"
                label="Brand Name"
                type="text"
                placeholder="Enter Brand Name"
              />
            </Col>
          </Row>
          <Row className="gy-4 mt-1">
            <Col lg="4">
              <FormInput
                name="deliveryLeadDays"
                label="Delivery Lead Days"
                required={true}
                type="number"
                pattern={/^\d+$/}
                placeholder="Enter Delivery Lead Days"
              />
            </Col>
          </Row>
        </div>

        <div className="form-section mb-4">
          <h5 className="mb-3 fw-bold">Contact Information</h5>
          <Row className="gy-4">
            <Col lg="4">
              <FormInput
                name="contactPerson"
                label="Contact Person"
                required={true}
                type="text"
                placeholder="Enter Contact Person"
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="phoneNumber"
                label="Phone Number"
                required={true}
                type="text"
                pattern={/^[6-9]\d{9}$/}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                }}
                placeholder="Enter Phone Number"
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="email"
                label="Email"
                required={true}
                type="email"
                maxLength={50}
                placeholder="Enter Email address"
                pattern={/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/}
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col lg="4">
              <FormSelect
                name="country"
                label="Country"
                options={countries}
                required={true}
                placeholder="-Select-"
              />
            </Col>
            <Col lg="4">
              <FormSelect
                name="state"
                label="State"
                required={true}
                options={states}
                placeholder="Select State"
              />
            </Col>
            <Col lg="4">
              <FormSelect
                name="district"
                label="District"
                options={districtOptions}
                required={true}
                placeholder="Select District"
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col lg="4">
              <FormInput
                name="city"
                label="City"
                required={true}
                placeholder="Enter City"
                type="text"
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="area"
                label="Area"
                required={true}
                placeholder="Enter Area"
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="pincode"
                label="Zip/Pincode"
                required={true}
                type="text"
                pattern={/^\d+$/}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                }}
                placeholder="Enter Zip or pin code"
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col lg="4">
              <FormInput
                name="addressLine1"
                label="Address Line 1"
                required={true}
                placeholder="Enter Site Address"
                type="text"
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="addressLine2"
                label="Address Line 2"
                placeholder="Enter Address Line 2"
                type="text"
              />
            </Col>
          </Row>
        </div>

        <div className="form-group mt-5">
          <Row>
            {onBack && (
              <Col lg="auto" md="auto" sm="auto">
                <Button
                  type="button"
                  color="secondary"
                  size="lg"
                  onClick={onBack}
                >
                  Back
                </Button>
              </Col>
            )}
            <Col lg="auto" md="auto" sm="auto">
              <Button type="submit" color="primary" size="lg">
                Submit
              </Button>
            </Col>
          </Row>
        </div>
      </form>

      <GstinVerificationModal
        isOpen={gstinModal}
        toggle={() => setGstinModal(!gstinModal)}
        onVerified={handleGSTVerification}
        countries={countries}
        states={states}
        districts={districtOptions}
        source="suppliers"
      />
    </>
  );
};
